import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Modal,
  Fade,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoPrefeitura from "../../assets/images/logo-prefeitura.png";
import { recuperarSenhaCandidato } from "../../services/candidato";
import { formatarCpf, validarCpf } from "../../utils/normalization";

const RecuperarSenhaCandidato: React.FC = () => {
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return numbers.replace(/^(\d{2})(\d{0,5})$/, "($1) $2");
    } else if (numbers.length <= 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{0,4})$/, "($1) $2-$3");
    }

    return numbers
      .slice(0, 11)
      .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate("/login-candidato");
  };

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validar CPF
    const cpfNumerico = cpf.replace(/\D/g, "");
    if (!validarCpf(cpfNumerico)) {
      setErrorMessage("CPF inválido! Por favor, insira um CPF válido.");
      setOpenErrorModal(true);
      setLoading(false);
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("E-mail inválido! Por favor, insira um e-mail válido.");
      setOpenErrorModal(true);
      setLoading(false);
      return;
    }

    const telefoneNumerico = telefone.replace(/\D/g, "");

    const recuperarData = {
      cpf: cpfNumerico,
      telefone: telefoneNumerico,
      email: email.toLowerCase().trim(),
    };

    try {
      const response = await recuperarSenhaCandidato(recuperarData);

      if (response.data?.success) {
        setOpenSuccessModal(true);
      } else {
        setErrorMessage(
          response.data?.message || "Erro ao processar solicitação."
        );
        setOpenErrorModal(true);
      }
    } catch (error: any) {
      console.error("Erro ao recuperar senha:", error);

      if (error?.response?.data) {
        const apiResponse = error.response.data;
        setErrorMessage(
          apiResponse.message || "Dados não encontrados. Verifique as informações."
        );
      } else {
        setErrorMessage("Erro de conexão com o servidor.");
      }
      setOpenErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const phoneNumbers = telefone.replace(/\D/g, "");
    const cpfNumbers = cpf.replace(/\D/g, "");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return (
      cpfNumbers.length === 11 &&
      phoneNumbers.length >= 10 &&
      phoneNumbers.length <= 11 &&
      emailRegex.test(email)
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            maxWidth: 450,
            margin: "0 auto",
            minHeight: 650,
          }}
        >
          {/* Botão Voltar */}
          <Box sx={{ width: "100%", mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/login-candidato")}
              sx={{
                textTransform: "none",
                color: "#666",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Voltar
            </Button>
          </Box>

          {/* Logo */}
          <Box
            component="img"
            src={LogoPrefeitura}
            alt="Logo Prefeitura"
            sx={{
              width: 200,
              height: "auto",
              mb: 3,
              objectFit: "contain",
            }}
          />

          {/* Título */}
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 600, mb: 2, color: "#333", textAlign: "center" }}
          >
            Recuperar Senha
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 4,
              color: "#666",
              textAlign: "center",
              px: 2,
            }}
          >
            Informe os dados abaixo para recuperar sua senha. Você receberá a nova senha no e-mail informado.
          </Typography>

          {/* Formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="CPF"
                value={cpf}
                onChange={(e) => {
                  const formatted = formatarCpf(e.target.value);
                  setCpf(formatted);
                }}
                margin="normal"
                required
                placeholder="000.000.000-00"
                variant="outlined"
                autoFocus
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Telefone"
                value={telefone}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setTelefone(formatted);
                }}
                margin="normal"
                required
                placeholder="(51) 99999-8080"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                placeholder="seu@email.com"
                variant="outlined"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!isFormValid() || loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  backgroundColor: "#2196f3",
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "ENVIAR"
                )}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Lembrou sua senha?{" "}
                  <Button
                    variant="text"
                    onClick={() => navigate("/login-candidato")}
                    sx={{
                      textTransform: "none",
                      color: "#1976d2",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Fazer Login
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Modal de Sucesso */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openSuccessModal}
        onClose={handleCloseSuccessModal}
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openSuccessModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #4caf50",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 60, color: "success.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
              sx={{ mb: 2 }}
            >
              Solicitação Enviada!
            </Typography>
            <Typography id="transition-modal-description" sx={{ mb: 3 }}>
              As instruções para recuperação de senha foram enviadas para o e-mail informado.
              Por favor, verifique sua caixa de entrada e spam.
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseSuccessModal}
              sx={{ mt: 2 }}
            >
              OK
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* Modal de Erro */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #f44336",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 60, color: "error.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
              sx={{ mb: 2 }}
            >
              Erro na Solicitação
            </Typography>
            <Typography id="transition-modal-description" sx={{ mb: 3 }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseErrorModal}
              sx={{ mt: 2 }}
              color="error"
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default RecuperarSenhaCandidato;